import React, { useState } from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMotionValue } from 'framer-motion';
import { Container } from '../Container';
import { Section } from '../Section';
import { ScrollReveal } from '../ScrollReveal';
import { projects } from '../../data/projects';
import type { Project } from '../../types/content';
import { BrowserMockup } from './BrowserMockup';
import { CaseStudyCursor } from './CaseStudyCursor';
interface ProjectCardProps {
  project: Project;
  number: string;
  layout: 'featured' | 'secondary';
  revealDelay: number;
}
function AnimatedBottomBorder() {
  return <div className="relative mt-8 h-px w-full bg-[#E6E6E2]" data-id="element-576" data-mp="hewhii2g924x">
      <span aria-hidden="true" className="absolute inset-y-0 left-0 h-px w-full origin-left scale-x-0 bg-ink transition-transform duration-500 ease-editorial group-hover:scale-x-100 group-focus-visible:scale-x-100" data-id="element-577" data-mp="dfxe51wd9dfl" />
    </div>;
}
function ProjectMeta({
  project,
  size
}: {
  project: Project;
  size: 'featured' | 'secondary';
}) {
  return <>
      {project.isConcept && <p className="mb-3 text-[12px] uppercase tracking-[0.06em] text-[#6E6E6E]" data-id="element-578" data-mp="1f9bhvth8tdyx">
          Self-initiated concept
        </p>}
      <p className="text-small text-muted" data-id="element-579" data-mp="10l6ysoea30fm">
        {project.client} <span aria-hidden="true" data-id="element-580" data-mp="15ji6otl0j0v7">·</span> {project.clientType}
      </p>
      <h3 className={`mt-3 text-ink leading-[1.2] tracking-[-0.025em] ${size === 'featured' ? 'max-w-[20ch] text-[36px]' : 'max-w-[24ch] text-[24px]'}`} data-id="element-581" data-mp="rl21x86pnkfp">
        {project.headline}
      </h3>
      <div className={`flex items-baseline gap-5 ${size === 'featured' ? 'mt-6' : 'mt-5'} justify-between`} data-id="element-582" data-mp="hnnx65u5gk38">
        <p className="max-w-[24ch] text-small font-medium leading-6 text-ink" data-id="element-583" data-mp="mmh054bt8sp5">{project.outcome}</p>
        <p className="shrink-0 text-small text-muted" data-id="element-584" data-mp="2c1x84ge008dg">{project.year}</p>
      </div>
    </>;
}
function ProjectCard({
  project,
  number,
  layout,
  revealDelay
}: ProjectCardProps) {
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);
  const [isActive, setIsActive] = useState(false);
  const handleMouseMove = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    cursorX.set(event.clientX - rect.left);
    cursorY.set(event.clientY - rect.top);
  };
  return <ScrollReveal delay={revealDelay} data-id="element-585" data-mp="21l4raehlr417">
      <Link to={`/work/${project.slug}`} className="group relative block overflow-hidden rounded-card focus-visible:outline-none" onMouseMove={handleMouseMove} onMouseEnter={() => setIsActive(true)} onMouseLeave={() => setIsActive(false)} data-id="element-586" data-mp="2y9aqglvr9483">
        <CaseStudyCursor isActive={isActive} x={cursorX} y={cursorY} data-id="element-587" data-mp="2ki5y84upve7s" />

        {layout === 'featured' ? <div className="grid gap-10 lg:grid-cols-5 lg:items-center lg:gap-16" data-id="element-588" data-mp="3gzsg2hlajjp8">
            <div className="relative order-2 lg:order-1 lg:col-span-2" data-id="element-589" data-mp="3c92ab38ydeu0">
              <span aria-hidden="true" className="absolute -left-1 -top-2 select-none font-serif text-[96px] leading-none tracking-[-0.08em] text-transparent [-webkit-text-stroke:1px_#E6E6E2] lg:hidden" data-id="element-590" data-mp="3c6po2dw5wib5">
                {number}
              </span>
              <ProjectMeta project={project} size="featured" data-id="element-591" data-mp="17syu67ttb52f" />
            </div>
            <div className="relative order-1 lg:order-2 lg:col-span-3" data-id="element-592" data-mp="1cride0h0u2r3">
              <span aria-hidden="true" className="absolute -left-5 -top-10 z-0 hidden select-none font-serif text-[96px] leading-none tracking-[-0.08em] text-transparent [-webkit-text-stroke:1px_#E6E6E2] lg:block" data-id="element-593" data-mp="1hnz4n3jiox8e">
                {number}
              </span>
              <BrowserMockup image={project.screenshot} alt={project.screenshotAlt} viewportAspect="aspect-[16/11]" revealOnHover className="relative z-10" data-id="element-594" data-mp="1vf59zg9h9p37" />
            </div>
          </div> : <div data-id="element-595" data-mp="1rqkyv8h6t20q">
            <div className="relative" data-id="element-596" data-mp="1rqb477l766op">
              <span aria-hidden="true" className="absolute -left-3 -top-9 z-0 select-none font-serif text-[96px] leading-none tracking-[-0.08em] text-transparent [-webkit-text-stroke:1px_#E6E6E2]" data-id="element-597" data-mp="1mssz5saawblz">
                {number}
              </span>
              <BrowserMockup image={project.screenshot} alt={project.screenshotAlt} viewportAspect="aspect-[16/12]" revealOnHover className="relative z-10" data-id="element-598" data-mp="18t1mbuvit19k" />
            </div>
            <div className="mt-8" data-id="element-599" data-mp="1isir8a2y7b4h">
              <ProjectMeta project={project} size="secondary" data-id="element-600" data-mp="1ds4jyu96857i" />
            </div>
          </div>}

        <AnimatedBottomBorder data-id="element-601" data-mp="2u64fzu4wbaph" />
      </Link>
    </ScrollReveal>;
}
export function SelectedWork() {
  const [featuredProject, ...secondaryProjects] = projects.slice(0, 3);
  return <Section id="work" labelledBy="selected-work-title" className="overflow-hidden" data-id="element-602" data-mp="2wqsmthaogdmx">
      <Container data-id="element-603" data-mp="2wsvjgk6c550m">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end" data-id="element-604" data-mp="31pvpww73k9s9">
          <div className="lg:col-span-6" data-id="element-605" data-mp="31r6x75zr3zo4">
            <p className="text-small text-muted" data-id="element-606" data-mp="2nqmcu5e6tvsi">Selected work / 2026</p>
            <h2 id="selected-work-title" className="mt-4 max-w-[14ch] text-[40px] leading-[1.05] tracking-[-0.035em] text-ink lg:text-[56px]" data-id="element-607" data-mp="2nsh0vskra40t">
              Five concepts, five problems worth solving.
            </h2>
          </div>
          <div className="lg:col-span-3 lg:col-start-10" data-id="element-608" data-mp="3kfvab5rzt0pc">
            <p className="max-w-[25ch] text-small leading-6 text-muted" data-id="element-609" data-mp="36pi3j6qu7s1l">
              Five different companies. One standard: every design decision has a job to do.
            </p>
            <Link to="/work" className="group mt-6 inline-flex items-center gap-2 text-small text-accent transition-colors duration-150 ease-out hover:text-ink" data-id="element-610" data-mp="2b0j9g9vy01p">
              View all work
              <ArrowUpRightIcon size={16} aria-hidden="true" className="transition-transform duration-200 ease-editorial group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-611" data-mp="gwfrpcvpc5ay" />
            </Link>
          </div>
        </div>

        <div className="mt-20 lg:mt-28" data-id="element-612" data-mp="2yhcy6dgy6ux">
          <ProjectCard project={featuredProject} number="01" layout="featured" revealDelay={0} data-id="element-613" data-mp="2xfydrx8v28g" />

          <div className="mt-20 grid gap-x-10 gap-y-20 md:grid-cols-2 lg:mt-24" data-id="element-614" data-mp="3u5aghgvqii2m">
            {secondaryProjects.map((project, index) => <ProjectCard key={project.slug} project={project} number={`0${index + 2}`} layout="secondary" revealDelay={(index + 1) * 0.1} data-id="element-615" data-mp="3lb5rz442piq9" data-mpi={index} />)}
          </div>
        </div>
      </Container>
    </Section>;
}